import * as React from 'react';
import { CaretRight } from '../design-system/icons';
import { cn } from '../lib/utils';

export interface BreadcrumbCrumb {
  label: string;
  onClick?: () => void;
  /** Real URL: renders an anchor (open-in-new-tab, copy link) that still navigates in-app via onClick. */
  href?: string;
  icon?: React.ComponentType<{ size?: number }>;
}

// Below md only the campaign name and the leaf fit: Home and Campaign (and the carets before them) are hidden.
const HIDDEN_BELOW_MD = 'hidden md:inline-flex';

export function CampaignBreadcrumb({ crumbs }: { crumbs: BreadcrumbCrumb[] }) {
  return (
    <nav aria-label="Breadcrumb" className="flex min-w-0 max-w-full items-center gap-2 text-body-sm-medium">
      {crumbs.map((c, i) => {
        const isLast = i === crumbs.length - 1;
        const Icon = c.icon;
        const isStatic = isLast || !c.onClick;
        const hideBelowMd = i < 2;
        const inner = (
          <span className={cn('inline-flex min-w-0 max-w-full items-center gap-1.5', isStatic && 'text-foreground')}>
            {Icon ? <Icon size={16} /> : null}
            <span className="block max-w-full truncate">{c.label}</span>
          </span>
        );
        return (
          <React.Fragment key={`${c.label}-${i}`}>
            {i > 0 && <CaretRight size={14} aria-hidden className={cn('shrink-0 text-muted-foreground/60', i < 3 && 'hidden md:block')} />}
            {isStatic ? (
              <span className="min-w-0 text-foreground" aria-current={isLast ? 'page' : undefined}>
                {inner}
              </span>
            ) : c.href ? (
              <a
                href={c.href}
                onClick={(e) => {
                  if (!c.onClick || e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
                  e.preventDefault();
                  c.onClick();
                }}
                className={cn('min-w-0 max-w-full overflow-hidden text-muted-foreground transition-colors hover:text-foreground', hideBelowMd && HIDDEN_BELOW_MD)}
              >
                {inner}
              </a>
            ) : (
              <button
                type="button"
                onClick={c.onClick}
                className={cn('min-w-0 max-w-full overflow-hidden text-muted-foreground transition-colors hover:text-foreground', hideBelowMd && HIDDEN_BELOW_MD)}
              >
                {inner}
              </button>
            )}
          </React.Fragment>
        );
      })}
    </nav>
  );
}
